import React, { useEffect, useRef, useState } from 'react';
import { Bot, CheckCircle2, ExternalLink, Loader2, MinusCircle, XCircle } from 'lucide-react';
import { btnFieldAction } from './constants';
import { extractSigningDate, firstPdf, renderPdfPage } from '../../utils/fsDateExtract';
import { fetchJSON } from '../../utils/api';

// Signed Financial Statements — AI review (docs/modules/workflow-fs.md §7). ONE button runs the
// whole gate: the client-side signing-date detection (pdf.js) first, then the
// server-side AI checks (handwritten firm code + BizFile match). Every check
// is reported as a numbered ✓/✗ line, closed by the verdict:
//   - pass  → the submission is approved automatically on submit; neither the
//             signing-date field nor the reviewer appears.
//   - fail  → manual review: the requester types the signing date themselves
//             and picks a reviewer (both revealed only then).
// Both page images (page 1 + the signing-date page, date highlighted) are
// shown as small thumbnails under the checklist.
//
// The review id rides on the submission as the auto-approval voucher, bound
// server-side to the PDF's hash — changing the file or engagement resets the
// outcome here, and the server re-checks the same binding on submit.

const box = (tone) => ({
  green: 'mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800',
  amber: 'mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800',
  blue: 'mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800',
}[tone]);

const Row = ({ n, state, text, sub }) => (
  <li className="flex gap-2 text-sm text-slate-700">
    {state === 'pass' && <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />}
    {state === 'fail' && <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />}
    {state === 'skip' && <MinusCircle className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />}
    <div className="min-w-0">
      <p className={state === 'skip' ? 'text-slate-500' : ''}>{n}. {text}</p>
      {(sub || []).map((s, i) => <p key={i} className="text-xs text-slate-500 mt-0.5">{s}</p>)}
    </div>
  </li>
);

const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// The numbered checklist, shared by the New form (fresh run) and the detail
// page (stored verdict of an auto-approved submission). `conditions` is the
// server report — the New form synthesises the same shape for client-side
// failures (no PDF / scanned copy) that never reach the server.
export const FsAiConditionsList = ({ conditions }) => {
  if (!conditions) return null;
  const sd = conditions.signingDate || {};
  const hw = conditions.handwrittenCode || {};
  const cm = conditions.companyMatch || {};

  // 1 — signing date detected.
  const sdText = sd.passed
    ? <>Signing date {sd.raw ? `"${sd.raw}"` : fmtDay(sd.signingDate)} detected on page {sd.pageNo} of the Independent Auditor&rsquo;s Report.</>
    : sd.detectStatus === 'no_text'
      ? 'The PDF has no readable text layer (it looks like a scanned copy) — the signing date could not be detected.'
      : sd.detectStatus === 'unreadable'
        ? 'The PDF could not be opened (it may be corrupted or password-protected).'
        : sd.detectStatus === 'no_pdf'
          ? 'None of the selected files is a PDF — the signing date could not be detected.'
          : 'The signing date could not be identified in the auditor’s report.';

  // 2 — distance from the FYE the report states about itself.
  let fyeState; let fyeText;
  if (sd.passed && sd.fye) {
    fyeState = 'pass';
    fyeText = <>The signing date is {sd.daysFromFye} day{sd.daysFromFye === 1 ? '' : 's'} after the financial year end stated in the report ({fmtDay(sd.fye)}).</>;
  } else if (sd.passed) {
    fyeState = 'pass';
    fyeText = 'The report does not state its financial year end — the 1–400 day plausibility window was not applied.';
  } else if (sd.rejected) {
    fyeState = 'fail';
    fyeText = <>A date &ldquo;{sd.rejected.raw}&rdquo; on page {sd.rejected.page} was rejected — {sd.rejected.daysFromFye} days from the financial year end stated in the report{sd.fye ? ` (${fmtDay(sd.fye)})` : ''}, outside the 1–400 day window.</>;
  } else {
    fyeState = 'skip';
    fyeText = 'Not checked — no signing date was detected.';
  }

  // 3 — handwritten firm code.
  const hwSub = [];
  if ((hw.transcriptions || []).length > 0) {
    hwSub.push(`AI transcription of the handwriting: ${hw.transcriptions.map((t) => `“${t}”`).join(', ')}`);
  }
  if (hw.notes) hwSub.push(`AI notes: ${hw.notes}`);
  const hwText = hw.skipped ? 'Not checked — the signing date was not detected.'
    : hw.error ? `Handwritten firm code check failed: ${hw.error}`
      : hw.passed ? <>Handwritten firm code matched &ldquo;{hw.matchedCompany}&rdquo; on the signing-date page.</>
        : hw.handwritingPresent === false
          ? 'No handwriting was found on the signing-date page.'
          : 'No handwritten firm code on the signing-date page matched the Staff Company list.';

  // 4 — company vs BizFile.
  const cmSub = [];
  if (cm.fsName || cm.bizfileName) {
    cmSub.push(`Financial statements page 1: ${cm.fsName || '—'}${cm.fsUen ? ` (${cm.fsUen})` : ''} · BizFile: ${cm.bizfileName || '—'}${cm.bizfileUen ? ` (${cm.bizfileUen})` : ''}`);
  }
  if (cm.reason) cmSub.push(`AI reason: ${cm.reason}`);
  const cmText = cm.skipped ? 'Not checked — the signing date was not detected.'
    : cm.missingBizfile ? 'No BizFile is on file for this engagement — the company could not be verified.'
      : cm.error ? `Company check failed: ${cm.error}`
        : cm.passed ? <>The company on page 1 matches the BizFile (matched on {cm.matchedOn === 'uen' ? 'UEN' : 'name'}).</>
          : 'The AI did not judge the financial statements and the BizFile to be the same company.';

  return (
    <ul className="space-y-2">
      <Row n={1} state={sd.passed ? 'pass' : 'fail'} text={sdText} />
      <Row n={2} state={fyeState} text={fyeText} />
      <Row n={3} state={hw.skipped ? 'skip' : hw.passed ? 'pass' : 'fail'} text={hwText} sub={hwSub} />
      <Row n={4} state={cm.skipped ? 'skip' : cm.passed ? 'pass' : 'fail'} text={cmText} sub={cmSub} />
    </ul>
  );
};

// Small side-by-side page thumbnails (page 1 + signing-date page); click
// opens full size. Shared look for the New form (blob URLs) and the detail
// page (server evidence-image URLs).
export const FsAiPageThumbs = ({ images }) => {
  const shown = (images || []).filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-3">
      {shown.map((img) => (
        <div key={img.caption} className="w-52">
          {/* The caption truncates (full text in the tooltip); the link never
              wraps — "Full Size" breaking into two lines reads broken. */}
          <div className="flex items-center justify-between gap-2 text-xs text-slate-500 mb-1">
            <span className="truncate" title={img.caption}>{img.caption}</span>
            <a href={img.url} target="_blank" rel="noreferrer"
              className="shrink-0 whitespace-nowrap inline-flex items-center gap-1 underline hover:text-slate-700">
              <ExternalLink className="w-3 h-3" />Full Size
            </a>
          </div>
          <a href={img.url} target="_blank" rel="noreferrer" className="block bg-white border border-slate-200 rounded-md overflow-hidden">
            <img src={img.url} alt={img.caption} className="w-full h-auto block" />
          </a>
        </div>
      ))}
    </div>
  );
};

// Client-side failures that never reach the server still render the same
// checklist shape the server stores.
const localConditions = (state) => {
  const det = state.detection;
  const found = det && det.status === 'found';
  const signingDate = found
    ? { passed: true, signingDate: det.date, pageNo: det.page, raw: det.raw, fye: det.fye, daysFromFye: det.daysFromFye }
    : {
      passed: false,
      detectStatus: det ? det.status : 'no_pdf',
      fye: det?.fye || null,
      rejected: det?.rejected || null,
    };
  const rest = found
    ? { passed: false, error: state.error || 'AI review is not available on this server.' }
    : { passed: false, skipped: true };
  return { signingDate, handwrittenCode: { ...rest }, companyMatch: { ...rest } };
};

/**
 * State machine + UI for the New form. Inputs:
 *   enabled      server says AI review is configured (options.aiReview.enabled)
 *   engagementId selected engagement (single coverage)
 *   files        the selected upload files (the first PDF is the one reviewed)
 *   onDetected   (isoDate) => void — fired when detection finds the date, so
 *                the manual-lane date field arrives pre-filled (still editable)
 *   disabled     while saving
 *
 * Outputs the form consumes:
 *   busy          detection or AI call in flight (block Submit)
 *   passed        AI review passed — submit with aiReviewId, no reviewer, no date field
 *   aiReviewId    the voucher to POST
 *   detectedDate  the detected signing date (what a passing submission files)
 *   needsReviewer manual lane is on — reveal the date field + reviewer section
 *   gap           truthy while the mandatory AI review has not run to a verdict
 *   panel         everything rendered under the button
 *   run / ready   start the review from outside (the batch page's "Run AI
 *                 Review for All") / whether there is anything to run
 */
export const useFsAiReview = ({ enabled, engagementId, files, onDetected, disabled = false }) => {
  const [state, setState] = useState({ phase: 'idle' });
  const [progress, setProgress] = useState(null);
  const [thumbs, setThumbs] = useState(null); // { first: url, page: url, pageNo }
  const cancelRef = useRef(false);

  const pdf = firstPdf(files || []);
  const hasFiles = (files || []).length > 0;
  const noPdf = hasFiles && !pdf;

  // Any change of file or engagement invalidates the outcome (the server
  // would reject the stale voucher on submit anyway).
  useEffect(() => { setState({ phase: 'idle' }); setThumbs(null); }, [pdf, engagementId]);
  useEffect(() => () => {
    if (thumbs) { URL.revokeObjectURL(thumbs.first); URL.revokeObjectURL(thumbs.page); }
  }, [thumbs]);
  useEffect(() => () => { cancelRef.current = true; }, []);

  const busy = state.phase === 'detecting' || state.phase === 'reviewing';
  useEffect(() => {
    if (!busy) return undefined;
    const guard = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [busy]);

  const run = async () => {
    if (!pdf || !engagementId || busy) return;
    cancelRef.current = false;
    setProgress(null);
    setThumbs(null);
    setState({ phase: 'detecting' });

    let det;
    try {
      det = await extractSigningDate(pdf, {
        onProgress: setProgress,
        isCancelled: () => cancelRef.current,
      });
    } catch { det = { status: 'unreadable' }; }
    if (cancelRef.current || det.status === 'cancelled') { setState({ phase: 'idle' }); return; }

    if (det.status !== 'found') {
      // Condition 1 failed on the client — manual lane, no server round-trip.
      setState({ phase: 'done', detection: det, review: null, passed: false });
      return;
    }
    if (onDetected) onDetected(det.date);

    // Display thumbnails: page 1 + the hit page with the date highlighted.
    let firstRender = null;
    try {
      const [first, hit] = await Promise.all([
        renderPdfPage(pdf, 1),
        renderPdfPage(pdf, det.page, { highlight: det.raw }),
      ]);
      firstRender = first;
      if (!cancelRef.current) {
        setThumbs({
          first: URL.createObjectURL(first.blob),
          page: URL.createObjectURL(hit.blob),
          pageNo: det.page,
        });
      }
    } catch { /* thumbnails are optional */ }

    setState({ phase: 'reviewing', detection: det });
    try {
      // Clean render (no highlight) for the AI + the stored evidence.
      const clean = await renderPdfPage(pdf, det.page);
      const firstClean = firstRender || await renderPdfPage(pdf, 1);
      const form = new FormData();
      form.set('engagementId', String(engagementId));
      form.set('signingDate', det.date);
      form.set('pageNo', String(det.page));
      form.set('raw', det.raw || '');
      if (det.fye) form.set('fyeDate', det.fye);
      if (det.daysFromFye != null) form.set('daysFromFye', String(det.daysFromFye));
      form.append('pdf', pdf);
      form.append('pageImage', clean.blob, `page-${det.page}.jpg`);
      form.append('firstPageImage', firstClean.blob, 'first-page.jpg');
      const data = await fetchJSON('/api/workflow/fs/ai-review', { method: 'POST', body: form });
      if (data.enabled === false) {
        setState({ phase: 'done', detection: det, review: null, passed: false, error: 'AI review is not configured on this server.' });
      } else {
        setState({ phase: 'done', detection: det, review: data.review, passed: !!data.review.passed });
      }
    } catch (e) {
      setState({ phase: 'done', detection: det, review: null, passed: false, error: e.message });
    }
  };

  const done = state.phase === 'done';
  const passed = done && state.passed;
  const detection = state.detection || null;
  const detectedDate = detection?.status === 'found' ? detection.date : null;
  const needsReviewer = enabled === false || noPdf || (done && !passed);
  const gap = enabled !== false && !noPdf && !done;
  const conditions = done ? (state.review ? state.review.conditions : localConditions(state)) : null;

  const canRun = !!pdf && !!engagementId && !busy && !disabled && !passed;
  const button = (
    <button type="button" onClick={run} disabled={!canRun} className={btnFieldAction}>
      {busy
        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />{state.phase === 'detecting' ? 'Detecting…' : 'Reviewing…'}</>
        : <><Bot className="w-3.5 h-3.5" />Run AI Review</>}
    </button>
  );

  const hint = noPdf ? null
    : !pdf ? 'Upload the signed financial statements as a PDF to enable the AI review.'
      : !engagementId ? 'Select the engagement first.'
        : done ? 'Changing the file or engagement resets the review.' : null;

  const panel = (
    <div>
      <div className="flex items-center gap-3">
        {button}
        {hint && <span className="text-xs text-slate-500">{hint}</span>}
      </div>

      {noPdf && (
        <div className={box('amber')}>
          The AI review needs a PDF of the signed financial statements — none of the selected files is a PDF.
          Enter the signing date below; the submission is still approved when you submit it.
        </div>
      )}

      {state.phase === 'detecting' && (
        <div className={box('blue')}>
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
            <span>
              Step 1 of 2 — detecting the signing date. Please keep this page open.
              {progress && ` Scanning page ${progress.scanned} of ${progress.total}…`}
            </span>
            <button
              type="button"
              onClick={() => { cancelRef.current = true; }}
              className="ml-auto shrink-0 text-xs font-medium underline hover:text-blue-900"
            >
              Cancel
            </button>
          </span>
        </div>
      )}
      {state.phase === 'reviewing' && (
        <div className={box('blue')}>
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
            Step 2 of 2 — the AI is checking the handwritten firm code and the BizFile match. Please keep this page open.
          </span>
        </div>
      )}

      {done && (
        <div className={box(passed ? 'green' : 'amber')}>
          <FsAiConditionsList conditions={conditions} />
          <p className="font-medium mt-3">
            {passed
              ? 'AI review passed — the checks above are recorded with this submission.'
              : 'The checks above did not pass — review the document yourself, then enter the signing date below. Submitting still approves the request.'}
          </p>
          <FsAiPageThumbs images={thumbs ? [
            { caption: 'Page 1', url: thumbs.first },
            { caption: `Page ${thumbs.pageNo} — signing date highlighted`, url: thumbs.page },
          ] : []} />
        </div>
      )}
    </div>
  );

  // `run` lets a page with several companies start every tab's review from
  // one button; `ready` = a PDF is there and no verdict yet (worth running).
  const ready = !!pdf && !!engagementId && !busy && !disabled && !done;
  return { busy, passed, aiReviewId: passed ? state.review.id : null, detectedDate, needsReviewer, gap, panel, run, ready };
};

export default useFsAiReview;
